/**
 * @fileoverview Slice Redux para gerenciamento de carrinho com sincronização remota.
 * Gerencia estado do carrinho local e sincronização com backend via sessão.
 * 
 * @module CartSlice
 * @since 1.0.0
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api.js';
import { mobilePersistence } from '../../utils/mobilePersistence.js';
import { mobileConfig } from '../../utils/mobileUtils.js';

/**
 * Thunk para carregar carrinho da sessão ativa.
 * @async
 * @function loadCartAsync
 * @returns {Promise<Array>} Dados do carrinho
 */
export const loadCartAsync = createAsyncThunk(
  'cart/loadCart',
  async (_, { rejectWithValue, getState }) => {
    try {
      // Verificar se usuário está autenticado
      const { auth } = getState();
      if (!auth.isAuthenticated) {
        console.log('🚫 [CART] Usuário não autenticado, pulando carregamento do carrinho');
        return [];
      }
      
      const response = await api.get('/users/session/shopcart', {
        timeout: mobileConfig.requestTimeout
      });
      const cartData = response.data?.data?.cart_data || response.data?.cart_data || [];
      // map: converte dados do servidor para formato do frontend preservando imagens
      const serverData = cartData.map(item => ({
        id: item.productId,
        nome: item.name,
        preco: Number(item.price) || 0,
        quantity: Number(item.quantity) || 1,
        marca: item.brand || '',
        imagem: item.image || item.imagem || '',
        categoria: item.category || ''
      }));
      
      // Salva dados do servidor localmente (apenas em mobile)
      mobilePersistence.saveCart(serverData);
      return serverData;
    } catch (error) {
      // Se carrinho não encontrado ou erro de rede, usa dados locais
      if (error.response?.status === 404 || error.response?.status === 501 || error.code === 'NETWORK_ERROR') {
        console.warn('Cart endpoint not available, using local storage');
        return mobilePersistence.loadCart();
      }
      return rejectWithValue(error.response?.data?.message || 'Erro ao carregar carrinho');
    }
  }
);

/**
 * Thunk para salvar carrinho no servidor (método legado).
 * @async
 * @function saveCartAsync
 * @param {Array} cartData - Dados do carrinho
 * @returns {Promise<void>}
 */
export const saveCartAsync = createAsyncThunk(
  'cart/saveCart',
  async (cartData, { rejectWithValue }) => {
    try {
      await api.post('/users/session/save-shopcart', { cartData });
      return cartData;
    } catch (error) {
      // Se endpoint não implementado, apenas avisa mas não falha
      if (error.response?.status === 404 || error.response?.status === 501) {
        console.warn('Save cart endpoint not available, cart saved locally only');
        return cartData;
      }
      return rejectWithValue(error.response?.data?.message || 'Erro ao salvar carrinho');
    }
  }
);

/**
 * Thunk para operações atômicas no carrinho (insert/update/delete).
 * @async
 * @function saveCartActionAsync
 * @param {string} action - Ação: 'insert', 'update' ou 'delete'
 * @param {Array} cartData - Dados do produto para a ação
 * @returns {Promise<void>}
 */
export const saveCartActionAsync = createAsyncThunk(
  'cart/saveCartAction',
  async ({ action, cartData }, { rejectWithValue }) => {
    try {
      await api.post('/users/session/save-shopcart', { action, cartData });
      return { action, cartData };
    } catch (error) {
      // Se endpoint não implementado, apenas avisa mas não falha
      if (error.response?.status === 404 || error.response?.status === 501) {
        console.warn('Save cart action endpoint not available, cart saved locally only');
        return { action, cartData };
      }
      return rejectWithValue(error.response?.data?.message || 'Erro ao processar ação do carrinho');
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    items: [],
    isLoading: false,
    error: null,
    lastSync: null,
    needsSync: false
  },
  reducers: {
    addToCart: (state, action) => {
      const product = action.payload;
      // Rejeita produtos inativos ou removidos
      if (product.ativo === false || product.ativo === 0 || product.removido === true || product.removido === 1) {
        return;
      }
      const existingItem = state.items.find(item => item.id === product.id);
      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        state.items.push({ ...product, quantity: 1 });
      }
      state.needsSync = true;
      // Salva imediatamente no localStorage (apenas em mobile)
      mobilePersistence.saveCart(state.items);
    },
    updateCartQuantity: (state, action) => {
      const { id, quantity } = action.payload;
      const item = state.items.find(item => item.id === id);
      if (item) {
        if (quantity <= 0) {
          state.items = state.items.filter(item => item.id !== id);
        } else {
          item.quantity = quantity;
        }
      }
      state.needsSync = true;
      // Salva imediatamente no localStorage (apenas em mobile)
      mobilePersistence.saveCart(state.items);
    },
    removeFromCart: (state, action) => {
      state.items = state.items.filter(item => item.id !== action.payload);
      state.needsSync = true;
      // Salva imediatamente no localStorage (apenas em mobile)
      mobilePersistence.saveCart(state.items);
    },
    clearCart: (state) => {
      state.items = [];
      state.needsSync = false; // Não sincronizar quando limpar localmente
    },
    clearCartWithSync: (state) => {
      state.items = [];
      state.needsSync = true; // Sincronizar quando explicitamente solicitado
    },
    clearError: (state) => {
      state.error = null;
    },
    loadFromLocalStorage: (state) => {
      const localData = mobilePersistence.loadCart();
      if (localData.length > 0) {
        state.items = localData;
        state.needsSync = true; // Marca para sincronizar com servidor quando possível
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Load cart
      .addCase(loadCartAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadCartAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
        state.lastSync = new Date().toISOString();
        state.needsSync = false; // Dados carregados do servidor não precisam sync
      })
      .addCase(loadCartAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Save cart (legado)
      .addCase(saveCartAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveCartAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.lastSync = new Date().toISOString();
        state.needsSync = false;
      })
      .addCase(saveCartAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Save cart action (atômico)
      .addCase(saveCartActionAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveCartActionAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.lastSync = new Date().toISOString();
        state.needsSync = false;
      })
      .addCase(saveCartActionAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

export const { addToCart, updateCartQuantity, removeFromCart, clearCart, clearCartWithSync, clearError, loadFromLocalStorage } = cartSlice.actions;
export default cartSlice.reducer;